// UTM параметры которые нужно отслеживать
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'fbclid',
]

// Ключ для хранения в sessionStorage
const UTM_STORAGE_KEY = 'jetly_utm_params'

export interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  utm_id?: string
  gclid?: string
  fbclid?: string
  landing_page?: string
  referrer?: string
}

/**
 * Извлекает UTM параметры из URL и сохраняет в sessionStorage
 * Вызывать при загрузке страницы
 */
export function captureUTMParams(): void {
  const urlParams = new URLSearchParams(window.location.search)
  const existingParams = getStoredUTMParams()

  let hasNewParams = false
  const newParams: UTMParams = { ...existingParams }

  // Извлекаем UTM параметры из URL
  UTM_PARAMS.forEach((param) => {
    const value = urlParams.get(param)
    if (value) {
      newParams[param as keyof UTMParams] = value
      hasNewParams = true
    }
  })

  // Сохраняем landing page только если это первый визит
  if (!existingParams.landing_page) {
    newParams.landing_page = window.location.pathname + window.location.search
  }

  // Сохраняем referrer только если это первый визит
  if (!existingParams.referrer && document.referrer) {
    newParams.referrer = document.referrer
  }

  // Сохраняем в sessionStorage
  if (hasNewParams || !existingParams.landing_page) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(newParams))
  }
}

/**
 * Получает сохранённые UTM параметры из sessionStorage
 */
export function getStoredUTMParams(): UTMParams {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

/**
 * Формирует строку с UTM параметрами для отправки с формой
 */
export function getUTMString(): string {
  const params = getStoredUTMParams()
  const parts: string[] = []

  if (params.utm_source) parts.push(`utm_source: ${params.utm_source}`)
  if (params.utm_medium) parts.push(`utm_medium: ${params.utm_medium}`)
  if (params.utm_campaign) parts.push(`utm_campaign: ${params.utm_campaign}`)
  if (params.utm_term) parts.push(`utm_term: ${params.utm_term}`)
  if (params.utm_content) parts.push(`utm_content: ${params.utm_content}`)
  if (params.utm_id) parts.push(`utm_id: ${params.utm_id}`)
  if (params.gclid) parts.push(`gclid: ${params.gclid}`)
  if (params.fbclid) parts.push(`fbclid: ${params.fbclid}`)
  if (params.landing_page) parts.push(`landing_page: ${params.landing_page}`)
  if (params.referrer) parts.push(`referrer: ${params.referrer}`)

  return parts.length > 0 ? parts.join(' | ') : 'Прямой заход'
}

/**
 * Добавляет UTM параметры к URL для редиректа
 */
export function appendUTMToUrl(url: string): string {
  const params = getStoredUTMParams()
  const urlObj = new URL(url, window.location.origin)

  // Добавляем только основные UTM параметры к URL
  UTM_PARAMS.forEach((param) => {
    const value = params[param as keyof UTMParams]
    if (value && !urlObj.searchParams.has(param)) {
      urlObj.searchParams.set(param, value)
    }
  })

  return urlObj.pathname + urlObj.search
}

/**
 * Возвращает объект с UTM данными для отправки на сервер
 */
export function getUTMDataForSubmission(): Record<string, string> {
  const params = getStoredUTMParams()
  const data: Record<string, string> = {}

  if (params.utm_source) data.utm_source = params.utm_source
  if (params.utm_medium) data.utm_medium = params.utm_medium
  if (params.utm_campaign) data.utm_campaign = params.utm_campaign
  if (params.utm_term) data.utm_term = params.utm_term
  if (params.utm_content) data.utm_content = params.utm_content
  if (params.utm_id) data.utm_id = params.utm_id
  if (params.gclid) data.gclid = params.gclid
  if (params.fbclid) data.fbclid = params.fbclid
  if (params.landing_page) data.landing_page = params.landing_page
  if (params.referrer) data.referrer = params.referrer

  return data
}
